import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import stationManagement from '../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

interface ChartWidth {
  type: 'percentage' | 'pixel';
  width: number;
}

/**
 * Custom square bar component
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => {}}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </>
  );
}
/**
 * Power Consumption Report Chart
 * @returns {any} The Power Consumption Report Chart component.
 */
function PowerConsumptionReportChart() {
  const { t, i18n } = useTranslation('stationManagement');
  const isRTL = i18n.dir() === 'rtl';
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartWidth, setChartWidth] = useState<ChartWidth>({
    type: 'percentage',
    width: 100,
  });

  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>
              {t('name')}: {data?.name}
            </p>
            <p style={{ color: '#2E9E3A' }}>
              {t('totalPowerConsumed')}: {ToFixedValue(Number(data?.totalPowerConsumed) / 1000)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param {'voltage' | 'current' | 'temperature'} range The range string.
   */
  const fetchPowerConsumedReport = async (range: 'voltage' | 'current' | 'temperature') => {
    setIsLoading(true);
    try {
      const response = await stationManagement.fetchReportPowerConsumed({
        timePeriod: range,
        chargingStation: selectedChargingPoint?.id,
      });
      if (response.status === 200) {
        const formatted = response.data
          .map((item: any) => ({
            name: item.name,
            totalPowerConsumed: item.totalPowerConsumed,
          }))
          // 🔽 higher power first → lower power next
          .sort((a: any, b: any) => b.totalPowerConsumed - a.totalPowerConsumed);

        setBarGraphData(formatted);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };
  useEffect(() => {
    fetchPowerConsumedReport('voltage');
  }, []);

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 50,
      });
    }
  }, [barGraphData]);

  return (
    <div className="power-consumed-chart-wrap">
      <div className="power-consumed-container">
        <div className="power-consumed-header">
          <div className="power-consumed-title">
            <p>{t('powerConsumed')}</p>
          </div>
        </div>
        <div className="power-consumed-container">
          <div className="power-consumed-chart">
            {isLoading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={240}>
                <BarChart data={barGraphData} margin={{ top: 10, bottom: 5 }}>
                  <XAxis
                    dataKey="name" // Assuming 'label' has 'Jan', 'Feb', etc.
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                    height={20}
                    tickFormatter={(value) =>
                      value?.length > 3 ? `${value.slice(0, 3)}...` : value
                    }
                  />
                  <YAxis
                    width={80}
                    tickFormatter={(value) => `${ToFixedValue(value / 1000)} kW`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666', fontWeight: 600 }}
                    padding={{ top: 10 }}
                    orientation={isRTL ? 'right' : 'left'}
                  />

                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="totalPowerConsumed"
                    name={t('inUse')}
                    fill="#4ABDFB"
                    shape={<TriangleBar />}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PowerConsumptionReportChart;
